/**
 * @file        src/pages/blog/BlogCategoryList.jsx
 * @description Blog Category List. Columns: # · Image · Name · Slug · Language ·
 *              Status · Action.
 * @author      Kinetika Labs
 * @created     2026-07-01
 * @changelog
 *   - 2026-07-01 Claude — Initial (Blog Management).
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Chip from '../../components/Chip';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { get, del } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';

export default function BlogCategoryList() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');

  const qc = useQueryClient();
  const params = { page, pageSize };
  if (applied) params.q = applied;

  const { data, isLoading } = useQuery({
    queryKey: qk.blogCategories.list(params),
    queryFn: () => get('/blog-categories', { params }),
    placeholderData: keepPreviousData,
  });

  const removeMut = useMutation({
    mutationFn: (id) => del(`/blog-categories/${id}`),
    onSuccess: () => {
      toast.ok('Deleted', 'Blog category removed.');
      qc.invalidateQueries({ queryKey: ['blog-categories'] });
    },
    onError: (err) => toast.err('Failed', err.response?.data?.error?.message || 'Could not delete'),
  });

  const rows = data?.data || [];
  const total = data?.meta?.total || 0;

  async function onDelete(row) {
    if (!(await confirm(`Delete blog category “${row.name}”?`))) return;
    removeMut.mutate(row.id);
  }

  return (
    <>
      <PageHead
        title="Blog Category List"
        subtitle="Blog Management · categories"
        actions={<Link to="/blog/categories/new" className="btn primary"><Icon name="plus" /> Add Blog Category</Link>}
      />

      <article className="panel glass">
        <form className="search-row" onSubmit={(e) => { e.preventDefault(); setPage(1); setApplied(search); }}>
          <div className="field">
            <label>Name</label>
            <input placeholder="Search category…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <button className="btn primary" type="submit"><Icon name="search" /> Search</button>
        </form>

        <div className="table-wrap">
          {isLoading ? <TableSkeleton /> : (
            <table className="table">
              <thead>
                <tr><th>#</th><th>Image</th><th>Name</th><th>Slug</th><th>Language</th><th>Status</th><th>Action</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={7} className="empty">No blog categories yet.</td></tr>
                ) : rows.map((c, idx) => (
                  <tr key={c.id}>
                    <td className="mono">{(page - 1) * pageSize + idx + 1}</td>
                    <td>{c.image
                      ? <img src={c.image} alt={c.name} style={{ width: 72, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                      : <span className="muted">—</span>}</td>
                    <td><b>{c.name}</b></td>
                    <td className="mono">{c.slug}</td>
                    <td className="mono">{c.language}</td>
                    <td><Chip tone={c.is_active ? 'ok' : 'rej'}>{c.is_active ? 'Active' : 'Inactive'}</Chip></td>
                    <td className="row-act">
                      <Link to={`/blog/categories/${c.id}/edit`} className="ib edit" aria-label="Edit"><Icon name="edit" /></Link>
                      <button className="ib del" aria-label="Delete" onClick={() => onDelete(c)}><Icon name="trash" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />
      </article>
    </>
  );
}
